import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-dim',
  secondary: 'bg-surface-variant text-on-surface hover:bg-surface-dim',
  outline: 'border border-input bg-surface text-on-surface hover:bg-surface-variant hover:border-primary/40',
  ghost: 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface',
  // bg-destructive (#b42318), not bg-danger (#f04438): white on the 500-weight
  // fill measured 3.0:1, below AA, on every delete/void/reverse button in the
  // app. `danger` stays the fill token for dots and badges.
  danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs',
};

// One height/radius ladder: 32 / 36 / 44 / 40 square, 8px radius (the control
// radius from tailwind.config), so a sm button beside an md one still lines up.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm gap-1.5',
  md: 'h-9 px-3.5 text-data-md gap-2',
  lg: 'h-11 px-5 text-data-lg gap-2',
  icon: 'h-10 w-10 p-0 justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150 ease-standard',
        // ring-ring at full strength: ring-primary/40 rendered #a89bea against
        // white, 2.4:1, under the 3:1 minimum for a non-text focus indicator.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
        'active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
